<template>
  <div>
    <v-row class="actions-bar pa-4 bar-shadow primary-background">
      <v-col>
        <h2>Divisions</h2>
      </v-col>
      <v-col class="col-max-width-fit-content">
        <v-row class="justify-end">
          <Button
            id="add-division-button"
            color="primary"
            @clickAction="openAddDialog"
          >
            Add Division
          </Button>
        </v-row>
      </v-col>
    </v-row>

    <v-row class="pa-4">
      <v-col>
        <v-card>
          <v-data-table
            id="divisions-data-table"
            no-data-text="No data available"
            disable-pagination
            hide-default-footer
            fixed-header
            :headers="headers"
            :items="divisions"
            :dense="denseTables"
            :height="defaultTableHeight"
            :sort-by="['name']"
          >
            <template #item.name="{ item }">
              {{ item.name }}
            </template>
            <template #item.can_create_projects_from_ui="{ item }">
              <v-switch
                :input-value="item.can_create_projects_from_ui"
                hide-details
                dense
                inset
                @change="toggleCanCreateProjectsFromUi(item, $event)"
              />
            </template>
            <template #item.actions="{ item }">
              <v-row>
                <v-btn
                  text
                  icon
                  small
                  @click.stop="editDivision(item)"
                >
                  <v-icon>mdi-pencil</v-icon>
                </v-btn>
                <v-btn
                  text
                  icon
                  small
                  @click.stop="openDeleteDialog(item)"
                >
                  <v-icon>delete_outline</v-icon>
                </v-btn>
              </v-row>
            </template>
            <template #footer>
              <v-divider />
              <v-row class="table-footer">
                <span class="footer-text">Total records: {{ divisions.length }}</span>
              </v-row>
            </template>
          </v-data-table>
        </v-card>
      </v-col>
    </v-row>

    <DivisionDialog
      :is-opened="isDialogOpened"
      :division="selectedDivision"
      @close="closeDialog"
      @save="saveDivision"
    />

    <DeletionConfirmationDialog
      :dialog="isDeleteDialogOpened"
      title="Confirmation"
      text="Are you sure you want to delete this division?"
      confirmation-button-text="Delete"
      @confirmationAction="confirmDelete"
    />
  </div>
</template>

<script>
import { mapActions, mapGetters } from 'vuex';
import { mapCacheActions } from 'vuex-cache';

import { Button, Dialog as DeletionConfirmationDialog } from '@/components/shared';
import { defaultTableHeight } from '@/constants/tableHeights';
import { NotificationHelper } from '@/helpers/notification.helper';

import DivisionDialog from './DivisionDialog.vue';

export default {
  name: 'DivisionsPage',
  components: {
    Button,
    DeletionConfirmationDialog,
    DivisionDialog,
  },
  data: () => ({
    defaultTableHeight,
    isDialogOpened: false,
    isDeleteDialogOpened: false,
    selectedDivision: null,
    divisionToDelete: null,
    headers: [
      {
        text: 'Name',
        value: 'name',
        class: ['projects-headers'],
      },
      {
        text: 'Can create projects from UI',
        value: 'can_create_projects_from_ui',
        class: ['projects-headers'],
        width: '220px',
      },
      {
        text: 'Actions',
        value: 'actions',
        class: ['projects-headers'],
        sortable: false,
        width: '120px',
      },
    ],
  }),
  computed: {
    ...mapGetters('user/settings', ['denseTables']),
    ...mapGetters('shared/divisions', ['divisions']),
  },
  created() {
    this.getDivisions();
  },
  methods: {
    ...mapCacheActions('shared/divisions', ['getDivisions']),
    ...mapActions('shared/divisions', ['createDivision', 'updateDivision', 'deleteDivision']),

    openAddDialog() {
      this.selectedDivision = null;
      this.isDialogOpened = true;
    },
    editDivision(division) {
      this.selectedDivision = division;
      this.isDialogOpened = true;
    },
    openDeleteDialog(division) {
      this.divisionToDelete = division;
      this.isDeleteDialogOpened = true;
    },
    closeDialog() {
      this.isDialogOpened = false;
      this.selectedDivision = null;
    },
    async saveDivision(divisionData) {
      try {
        if (divisionData.id) {
          await this.updateDivision({
            divisionId: divisionData.id,
            divisionData: {
              name: divisionData.name,
              can_create_projects_from_ui: divisionData.canCreateProjectsFromUi,
            },
          });
          NotificationHelper.showSuccess('Division updated successfully');
        } else {
          await this.createDivision({
            name: divisionData.name,
            can_create_projects_from_ui: divisionData.canCreateProjectsFromUi,
          });
          NotificationHelper.showSuccess('Division created successfully');
        }

        this.closeDialog();
      } catch (error) {
        NotificationHelper.showError('Failed to save division');
      }
    },
    async toggleCanCreateProjectsFromUi(division, canCreateProjectsFromUi) {
      try {
        await this.updateDivision({
          divisionId: division.id,
          divisionData: {
            name: division.name,
            can_create_projects_from_ui: canCreateProjectsFromUi,
          },
        });
        NotificationHelper.showSuccess('Division updated successfully');
      } catch (error) {
        NotificationHelper.showError('Failed to save division');
      }
    },
    async confirmDelete(confirmed) {
      if (!confirmed) {
        this.isDeleteDialogOpened = false;
        this.divisionToDelete = null;

        return;
      }

      try {
        await this.deleteDivision(this.divisionToDelete.id);
        NotificationHelper.showSuccess('Division deleted successfully');
        this.isDeleteDialogOpened = false;
        this.divisionToDelete = null;
      } catch (error) {
        NotificationHelper.showError('Failed to delete division');
        this.isDeleteDialogOpened = false;
      }
    },
  },
};
</script>

<style lang="less">
@import "~variables";

#divisions-data-table .projects-headers {
  font-size: @table-font-size;
}
</style>

